
import React, { useState, useContext, useEffect } from 'react'
import { SpinnerComponent } from 'react-element-spinner'
import { Button, Card, CardFooter, CardHeader, Col, Container, Label, Row, Table } from 'reactstrap'
import { useTranslation } from 'react-i18next'
import DatePicker from 'react-datepicker'
import dayjs from 'dayjs'
import "react-datepicker/dist/react-datepicker.css"

import Paginator from '../../components/Paginator/Paginator'
import PageTitle from '../../components/PageTitles/PageTitle'
import { SessionContext } from '../../contexts/SessionProvider'
import ReportingService from '../../services/ReportingService'
import SiteService from "../../services/SiteService";

const BusinessSalesReport = () =>
{
	const { t } = useTranslation()
	const [isBusy, setIsBusy] = useState(false)
	const sessionContext = useContext(SessionContext)
	const [reportRequest, setReportRequest] = useState(	{ pageIndex: 0, pageSize: 15, reportStartDate: '', reportEndDate: '' })
	const [pagingResult, setPagingResult] = React.useState({ pageCollection: [], totalRecords: 0 })
	
	useEffect(() =>
	{
		setReportRequest((previousReportRequest) =>
		{
			let currentReportRequest = { ...previousReportRequest }
			
			currentReportRequest.reportStartDate = new Date()
			currentReportRequest.reportStartDate.setMonth(currentReportRequest.reportStartDate.getMonth() - 1)
			
			currentReportRequest.reportEndDate = new Date()
			
			retrieveReport(currentReportRequest)
			
			return currentReportRequest
		})
		
	}, [])
	
	const retrieveReport = (request) =>
	{
		setIsBusy(true)
		ReportingService
			.getBusinessSalesReport(dayjs(request.reportStartDate).format('MMM DD, YYYY'), dayjs(request.reportEndDate).format('MMM DD, YYYY'), sessionContext.loginSession.user.userSession.sessionKey)
			.then(result =>
			{
				setIsBusy(false)
				sessionContext.updateSession(result.data.loginSession)
				
				setPagingResult((previousPagingResult) =>
				{
					let currentPagingResult = { ...previousPagingResult }
					currentPagingResult.pageCollection = result.data.actionResult
					currentPagingResult.totalRecords = result.data.actionResult.length
					
					return currentPagingResult
				})
			})
			.catch((error) =>
			{
				setIsBusy(false)
				SiteService.displayApiError(error)
			})
	}
	
	const handleGetReport = () =>
	{
		setReportRequest((previousReportRequest) => ({ ...previousReportRequest, pageIndex: 0 }))
		retrieveReport(reportRequest)
	}
	
	const handleReload = () =>
	{
		retrieveReport(reportRequest)
	}
	
	const handlePageChange = (pageIndex) =>
	{
		setReportRequest((previousReportRequest) => ({ ...previousReportRequest, pageIndex }))
	}

	const pagedItems = React.useMemo(() =>
	{
		const startIndex = reportRequest.pageIndex * reportRequest.pageSize
		return pagingResult.pageCollection.slice(startIndex, startIndex + reportRequest.pageSize)
	}, [pagingResult.pageCollection, reportRequest.pageIndex, reportRequest.pageSize])
	
	const handleReportRequestPropertyChange = (name, value) =>
	{
		setReportRequest((previousReportRequest) =>
		{
			let currentReportRequest = { ...previousReportRequest }
			currentReportRequest[name] = value
			
			return currentReportRequest
		})
	}
	
	const handleDownloadReport = () =>
	{
		ReportingService.downloadReportData(pagingResult.pageCollection, 'SalesReport')
	}
	
	return (
		<>
			<SpinnerComponent
				loading={ isBusy }
				position="global"
				color="#3578e5"
				secondaryColor="#f3f3f3"
				backgroundColor="#d3d3d3"
				spinnerType="circle-dots"
			/>
			<PageTitle
				heading={ t('Reports.SalesReport.Title') }
				subheading=""
				icon="pe-7s-news-paper icon-gradient bg-premium-dark"
			/>
			<Container className="mt--6" fluid>
				<Row>
					<div className="col">
						<Card>
							<CardHeader>
								<div style={{ marginLeft: 'auto' }}>
									<table style={{ marginRight:'10px', marginBottom:'10px' }}>
										<tbody>
											<tr>
												<td>&nbsp;</td>
												<td style={{ paddingRight:'5px', fontSize: 'smaller' }}>
													<Label style={{ marginBottom: '0' }}>From</Label>
												</td>
												<td style={{ paddingRight:'5px' }}>
													<DatePicker
														selected={ reportRequest.reportStartDate }
														onChange={ (date) => handleReportRequestPropertyChange('reportStartDate', date) }
													/>
												</td>
												<td style={{ paddingRight:'5px', fontSize: 'smaller' }}>
													<Label style={{ marginBottom: '0' }}>To</Label>
												</td>
												<td style={{ paddingRight:'5px' }}>
													<DatePicker
														selected={ reportRequest.reportEndDate }
														onChange={ (date) => handleReportRequestPropertyChange('reportEndDate', date) }
													/>
												</td>
												<td style={{ whiteSpace: 'nowrap' }}>
													<Button
														color="secondary"
														size="sm"
														onClick={ () => handleGetReport() }
													>
														{ t('Reports.SalesReport.Get_Report') }
													</Button>
												</td>
												<td style={{ whiteSpace: 'nowrap' }}>
													<Button
														color="secondary"
														size="sm"
														onClick={ () => handleDownloadReport() }
													>
														{ t('Reports.Download') }
													</Button>
												</td>
											</tr>
										</tbody>
									</table>
								</div>
							</CardHeader>
							<div className="table-responsive">
								<Table className="align-items-center table-flush">
									<thead className="thead-light">
										<tr>
											<th className="sort" data-sort="Sale_Date" scope="col">{ t('Reports.SalesReport.Sale_Date') }</th>
											<th className="sort" data-sort="Iteration_Sold" scope="col">{ t('Reports.SalesReport.Iteration_Sold') }</th>
											<th className="sort" data-sort="year" scope="col">{ t('Reports.SalesReport.Year') }</th>
											<th className="sort" data-sort="Make" scope="col">{ t('Reports.SalesReport.Make') }</th>
											<th className="sort" data-sort="Model" scope="col">{ t('Reports.SalesReport.Model') }</th>
											<th className="sort" data-sort="Trim" scope="col">{ t('Reports.SalesReport.Trim') }</th>
											<th className="sort" data-sort="Vin" scope="col">{ t('Reports.SalesReport.Vin') }</th>
											<th className="sort" data-sort="Lease_Number" scope="col">{ t('Reports.SalesReport.Lease_Number') }</th>
											<th className="sort" data-sort="Odometer" scope="col">{ t('Reports.SalesReport.Odometer') }</th>
											<th className="sort" data-sort="Grade" scope="col">{ t('Reports.SalesReport.Grade') }</th>
											<th className="sort" data-sort="Damages" scope="col">{ t('Reports.SalesReport.Damages') }</th>
											<th className="sort" data-sort="Total_Claims" scope="col">{ t('Reports.SalesReport.Total_Claims') }</th>
											<th className="sort" data-sort="Location" scope="col">{ t('Reports.SalesReport.Location') }</th>
											<th className="sort" data-sort="Purchase_Location" scope="col">{ t('Reports.SalesReport.Purchase_Location') }</th>
											<th className="sort" data-sort="Bids" scope="col">{ t('Reports.SalesReport.Bids') }</th>
											<th className="sort" data-sort="Reserve" scope="col">{ t('Reports.SalesReport.Reserve') }</th>
											<th className="sort" data-sort="Buyer" scope="col">{ t('Reports.SalesReport.Buyer') }</th>
											<th className="sort" data-sort="Dealer_Code" scope="col">{ t('Reports.SalesReport.Dealer_Code') }</th>
											<th className="sort" data-sort="Ownership_Status" scope="col">{ t('Reports.SalesReport.Ownership_Status') }</th>
											<th className="sort" data-sort="Transport" scope="col">{ t('Reports.SalesReport.Transport') }</th>
											<th className="sort" data-sort="Purchase_Time" scope="col">{ t('Reports.SalesReport.Purchase_Time') }</th>
											<th className="sort" data-sort="Purchase_Price" scope="col">{ t('Reports.SalesReport.Purchase_Price') }</th>
											<th className="sort" data-sort="Purchase_Tax" scope="col">{ t('Reports.SalesReport.Purchase_Tax') }</th>
											<th className="sort" data-sort="Carfax_Fee" scope="col">{ t('Reports.SalesReport.Carfax_Fee') }</th>
											<th className="sort" data-sort="Carfax_Tax" scope="col">{ t('Reports.SalesReport.Carfax_Tax') }</th>
											<th className="sort" data-sort="Buy_Fee" scope="col">{ t('Reports.SalesReport.Other_Fee') }</th>
											<th className="sort" data-sort="Buy_Fee_Tax" scope="col">{ t('Reports.SalesReport.Other_Fee_Tax') }</th>
											<th className="sort" data-sort="Debit_Total" scope="col">{ t('Reports.SalesReport.Debit_Total') }</th>
											<th className="sort" data-sort="Sale_Voided" scope="col">{ t('Reports.SalesReport.Sale_Voided') }</th>
											<th className="sort" data-sort="Void_Date" scope="col">{ t('Reports.SalesReport.Void_Date') }</th>
											<th className="sort" data-sort="Void_User_Id" scope="col">{ t('Reports.SalesReport.Void_User_Id') }</th>
											<th className="sort" data-sort="Void_Reason" scope="col">{ t('Reports.SalesReport.Void_Reason') }</th>
										</tr>
									</thead>
									<tbody className="list">
										{pagedItems.map((pageItem, idx) => (
											<tr key={ idx }>
												<td style={{ whiteSpace: 'nowrap' }}>{ dayjs(pageItem.saleDate).format('MMM D, YYYY') }</td>
												<td>{ pageItem.iterationSold }</td>
												<td>{ pageItem.year	}</td>
												<td>{ pageItem.make	}</td>
												<td>{ pageItem.model }</td>
												<td>{ pageItem.trim	}</td>
												<td>{ pageItem.serialNumber	}</td>
												<td>{ pageItem.leaseNumber }</td>
												<td>{ SiteService.numberDisplay(pageItem.mileage) }</td>
												<td>{ pageItem.grade }</td>
												<td>{ SiteService.priceDisplay(pageItem.damages) }</td>
												<td>{ pageItem.totalClaims }</td>
												<td>{ pageItem.location }</td>
												<td>{ pageItem.purchaseLocation }</td>
												<td>{ pageItem.bids }</td>
												<td>{ SiteService.priceDisplay(pageItem.reservePrice) }</td>
												<td style={{ whiteSpace: 'nowrap' }}>{ pageItem.buyer }</td>
												<td>{ pageItem.dealerCode }</td>
												<td style={{ whiteSpace: 'nowrap' }}>{ pageItem.ownershipRequest ? dayjs(pageItem.ownershipRequest).format('MMM D, YYYY') : '—' }</td>
												<td style={{ whiteSpace: 'nowrap' }}>{ pageItem.transportRequest ? dayjs(pageItem.transportRequest).format('MMM D, YYYY') : '—' }</td>
												<td style={{ whiteSpace: 'nowrap' }}>{ dayjs(pageItem.purchaseTime).format('MMM DD, YYYY HH:mm') }</td>
												<td>{ SiteService.priceDisplay(pageItem.purchasePrice) }</td>
												<td>{ SiteService.priceDisplay(pageItem.purchasePriceTax) }</td>
												<td>{ SiteService.priceDisplay(pageItem.carfaxFee) }</td>
												<td>{ SiteService.priceDisplay(pageItem.carfaxFeeTax) }</td>
												<td>{ SiteService.priceDisplay(pageItem.buyFee) }</td>
												<td>{ SiteService.priceDisplay(pageItem.buyFeeTax) }</td>
												<td>{ SiteService.priceDisplay(pageItem.debitTotal) }</td>
												<td>{ pageItem.saleVoided }</td>
												<td style={{ whiteSpace: 'nowrap' }}>{ pageItem.voidDate ? dayjs(pageItem.voidDate).format('MMM D, YYYY') : '' }</td>
												<td>{ pageItem.voidUserId }</td>
												<td>{ pageItem.voidReason }</td>
											</tr>
										))}
									</tbody>
								</Table>
							</div>
							<CardFooter className="py-4">
								{
									(pagingResult.pageCollection.length === 0) && (
										<Row style={{ marginBottom: '10px', width: '100%' }}>
											<Col md="12" style={{ textAlign: 'center' }}>
												<Label>{t('Reports.SalesReport.No_Results')}</Label>
											</Col>
										</Row>
									)
								}
								<nav aria-label="...">
									{
										(pagingResult.pageCollection.length > 0) && (
											<Paginator
												currentPage={ reportRequest.pageIndex }
												pages={ parseInt(((pagingResult.totalRecords - 1) / reportRequest.pageSize) + 1, 10) }
												selectPage={ handlePageChange }
											/>
										)
									}
								</nav>
							</CardFooter>
						</Card>
					</div>
				</Row>
			</Container>
		</>
	)
}

export default BusinessSalesReport
